import { skipToken } from '@apollo/client/react'
import { ToggleButton } from '@mui/material'
import type { ReactNode, RefObject } from 'react'
import { createContext, Suspense, useEffect, useMemo, useState } from 'react'
import {
  Tab as AriaTab,
  TabList as AriaTabList,
  TabPanel as AriaTabPanel,
  Tabs as AriaTabs,
} from 'react-aria-components'
import TaskDetailDialog from 'src/components/GenerationTask/TaskDetailDialog'
import { useTaskById } from 'src/services/apollo/task'
import { useQs } from 'src/utils/qs'
import type { BaseCursorPagingContextType } from '../CursorPaging/BaseCursorPaging'
import LoadingPage from '../LoadingPage'
import { StyledToggleButtonGroup } from '../OrderSelect'
import type { EditItemsState } from '../UserProfile/EditItems'

export default function ProfileTaskTab(props: {
  tabs: { value: string; label: ReactNode; children: ReactNode }[]
  currentTab: string
  onChange: (value: string) => any
}) {
  const { tabs, currentTab, onChange } = props

  /*
   * Open task directly
   */

  const [{ openTask: taskId }] = useQs()
  const { data: task } = useTaskById(taskId || skipToken, {
    context: { refetchOnFocus: false },
  })
  const [openTask, setOpenTask] = useState(false)

  useEffect(() => {
    if (task) {
      setOpenTask(true)
      // I decided not to auto-remove the param to leave it a chance to re-open
      // the task by refreshing the page
    }
  }, [task])

  const actualTabToggles = useMemo(() => {
    return (
      <StyledToggleButtonGroup
        exclusive
        onChange={(_, v?: string | null) => {
          if (v == null) return
          onChange(v)
        }}
        value={currentTab}
      >
        {tabs.map(({ value, label }) => {
          return (
            <ToggleButton key={value} value={value} className="normal-case">
              {label}
            </ToggleButton>
          )
        })}
      </StyledToggleButtonGroup>
    )
  }, [currentTab, onChange, tabs])

  return (
    <>
      <AriaTabs
        selectedKey={currentTab}
        onSelectionChange={val => onChange(val as string)}
      >
        {/* this acts as a registry */}
        <AriaTabList className="hidden">
          {tabs.map(({ value }) => (
            <AriaTab key={value} id={value} />
          ))}
        </AriaTabList>

        <Suspense fallback={<LoadingPage />}>
          {tabs.map(({ value, children }) => {
            return (
              <AriaTabPanel key={value} id={value}>
                {actualTabToggles}
                {children}
              </AriaTabPanel>
            )
          })}
        </Suspense>
      </AriaTabs>
      <TaskDetailDialog
        task={task}
        open={openTask}
        onClose={() => setOpenTask(false)}
      />
    </>
  )
}

export const TaskListEditContext = createContext<{
  pagingRef: RefObject<BaseCursorPagingContextType | null> | null
  editState: EditItemsState | null
}>({
  pagingRef: null,
  editState: null,
})
